import * as React from 'react';
import * as DateFns from 'date-fns';
import CreditCardInput from './CreditCardInput';
import CVVInput from './CVVInput';
import DateInput from './DateInput';
import LinkButton from './LinkButton';
import AuxiliaryCheckBox from './AuxiliaryCheckBox';
import '../css/addPayment.sass';
import { RulesMessages } from '../utils/validator';
import Formatter from './Formatter';
import '../css/input-text.sass';
import PaymentIcon from './PaymentIcon';

export interface CardDetails {
  cardBrand: string;
  cardNumber: string;
  expMonth: string;
  expYear: string;
  cvv: string;
  saveCard: boolean;
}

interface CardProps {
  newCardLabel?: string;
  cardExpLabel?: string;
  saveCardLabel?: string;
  addCardLink?: string;
  rulesMessages?: { [key: string]: RulesMessages };
  displayDetails?: boolean;
  hideSavePaymentInfo?: boolean;
  onBrandIdentified?: Function;
  onLast4Identified?: Function;
  onAddCard: (details: CardDetails) => void;
  hideCreditCardInput?: boolean;
  hideCVVInput?: boolean;
  cardNumber?: string;
  cardBrand?: string;
  expMonth?: string;
  expYear?: string;
  cvv?: string;
  toolTip?: React.ReactNode;
}

export default function NewCard(props: CardProps) {
  const {
    displayDetails = false,
    hideSavePaymentInfo = false,
    newCardLabel = 'New Card',
    cardExpLabel = 'Expiration Date',
    saveCardLabel = 'Save for later use',
    addCardLink = 'Add Card',
    hideCreditCardInput = false,
    hideCVVInput = false,
    cardNumber = '',
    cardBrand = 'NEWCARD',
    expMonth = '',
    expYear = '',
    cvv = ''
  } = props;
  const [state, setState] = React.useState<CardDetails>({
    cardBrand,
    cardNumber,
    expMonth,
    expYear,
    cvv,
    saveCard: true
  });

  return (
    <div className="addPayment">
      {displayDetails && (
        <>
          <div>{newCardLabel}</div>
          <div className="endingWith">
            <PaymentIcon
              className="brandImg"
              brand={state.cardBrand}
              brandName={state.cardBrand}
            />
            {!!state.cardNumber && (
              <span className="cardDetails">
                {' '}
                <Formatter
                  format={state.cardBrand}
                  type="card"
                  value={state.cardNumber}
                />
              </span>
            )}
          </div>
          <div className="hr">&nbsp;</div>
        </>
      )}
      {!hideCreditCardInput && (
        <div>
          <CreditCardInput
            name="cardNumber"
            id="cardNumber"
            rulesMessages={
              props.rulesMessages && props.rulesMessages.cardNumber
            }
            label={props.cardNumber}
            hasError={false}
            onComplete={(CardNumber: string) => {
              if (CardNumber) {
                const cardNumberComplete = CardNumber.replace(/ /gi, '');
                if (props.onLast4Identified) {
                  props.onLast4Identified(
                    cardNumberComplete.slice(cardNumberComplete.length - 4)
                  );
                }
                setState({ ...state, cardNumber: cardNumberComplete });
              } else {
                setState({ ...state, cardNumber: '' });
              }
            }}
            onError={() => {
              setState({ ...state, cardNumber: '' });
            }}
            onChange={(e: any) => {
              if (
                e.target.cardType &&
                e.target.cardType.toUpperCase() !== 'NEWCARD'
              ) {
                if (props.onBrandIdentified !== undefined) {
                  props.onBrandIdentified(e.target.cardType.toUpperCase());
                }
                setState({
                  ...state,
                  cardBrand: e.target.cardType.toUpperCase()
                });
              } else {
                setState({
                  ...state,
                  cardBrand: 'NEWCARD'
                });
                if (props.onBrandIdentified !== undefined) {
                  props.onBrandIdentified('NEWCARD');
                }
              }
            }}
            required
          />
        </div>
      )}
      <div className={`${hideCVVInput ? '' : 'align-fields'}`}>
        <div>
          <DateInput
            disablePastDates
            maxDate={DateFns.lastDayOfYear(DateFns.addYears(new Date(), 20))}
            name="cardExpDate"
            id="cardExpDate"
            rulesMessages={
              props.rulesMessages && props.rulesMessages.cardExpDate
            }
            label={cardExpLabel}
            onComplete={(value: any) => {
              if (value) {
                setState({
                  ...state,
                  expMonth: value.split('/')[0].replace(/\s/g, ''),
                  expYear: `20${value.split('/')[1].replace(/\s/g, '')}` // regex to replace the space
                });
              } else {
                setState({
                  ...state,
                  expMonth: '',
                  expYear: ''
                });
              }
            }}
            onError={() => {
              setState({ ...state, expMonth: '', expYear: '' });
            }}
            format="MM/yy"
            placeHolder="MM / YY"
            value={
              state.expYear && state.expMonth
                ? `${state.expMonth}/${state.expYear.slice(
                    state.expYear.length - 2
                  )}`
                : ''
            }
            required
          />
        </div>
        {!hideCVVInput && (
          <div>
            <CVVInput
              name="cvv"
              id="cvv"
              rulesMessages={props.rulesMessages && props.rulesMessages.cvv}
              label={props.cvv}
              hasError={false}
              onComplete={(CVV: string) => {
                if (CVV) {
                  const cvvComplete = CVV.trim();
                  setState({ ...state, cvv: cvvComplete });
                } else {
                  setState({ ...state, cvv: '' });
                }
              }}
              onError={() => {
                setState({ ...state, cvv: '' });
              }}
              onChange={() => {}}
              required
              toolTip={props.toolTip}
            />
          </div>
        )}
      </div>
      {!hideSavePaymentInfo && (
        <div>
          <AuxiliaryCheckBox
            name="saveCard"
            label={saveCardLabel}
            id="saveCard"
            checked={state.saveCard}
            hasError={false}
            onChange={(event: any) =>
              setState({
                ...state,
                saveCard: event.target.checked
              })
            }
          />
        </div>
      )}

      <div style={{ textAlign: 'right' }}>
        <LinkButton
          name="addCard"
          id="addCard"
          smol
          disabled={
            !(
              (hideCreditCardInput || !!state.cardNumber) &&
              !!state.expMonth &&
              !!state.expYear &&
              (hideCVVInput || !!state.cvv)
            )
          }
          onClick={() => {
            props.onAddCard(state);
          }}
        >
          {addCardLink}
        </LinkButton>
      </div>
    </div>
  );
}
